import type { input, output } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DialogType, StyleObj } from '../../../../../@types';
import { CRMApiEntityType, CreditActionType, TransactionType, WalletType } from '../../../../../@types/api';
import { QUERY_KEYS, WALLET_TYPE_OPTIONS } from '../../../../../constants';
import { useInvalidateQuery } from '../../../../../hooks/useInvalidateQuery';
import useMutateData from '../../../../../hooks/useMutateData';
import usePermissions from '../../../../../hooks/usePermissions';
import { handleCreditsSchema } from '../../../../../schema';
import FormNumberInput from '../../../../molecules/FormNumberInput';
import FormSelect from '../../../../molecules/FormSelect';
import {
  CREDIT_ACTION_TYPE,
  WALLET_TRANSACTION_SOURCE,
  WALLET_TRANSACTION_TYPE,
  WALLET_TYPE,
} from '../../../../../constants/enums';

const getTransactionType = (
  isOtherTransaction: boolean,
  walletType: WalletType,
  actionType: CreditActionType
): TransactionType => {
  if (isOtherTransaction) return WALLET_TRANSACTION_TYPE.OTHER;
  if (walletType === WALLET_TYPE.BONUS) return WALLET_TRANSACTION_TYPE.BONUS;

  return actionType;
};

const styles: StyleObj = {
  actions: { pl: 2 },

  checkbox: { alignSelf: 'start', '& .MuiFormControlLabel-label': { typography: { xs: 'body3', md: 'body2' } } },
};

type CreditFormInput = input<typeof handleCreditsSchema>;
type CreditFormData = output<typeof handleCreditsSchema>;

const DEFAULT_FORM_DATA: CreditFormInput = {
  walletType: 'main',
  amount: '',
  isOtherTransaction: false,
  description: '',
};

type CreditRequestData = Omit<CreditFormData, 'amount' | 'isOtherTransaction'> & {
  userId: string;
  isForAgentPayment: boolean;
  source: string;
  amount: number;
  transactionType: TransactionType;
  otherTransactionType: TransactionType; // Used to differentiate between Other deposits and withdrawals
  currency?: string;
};

type Props = DialogType & {
  item: {
    userId: string;
    userType: CRMApiEntityType;
  };
};

const HandleCreditsForm = ({ closeModal, item }: Props) => {
  const { userId, userType } = item;

  const invalidateData = useInvalidateQuery();
  const { hasPermission } = usePermissions();

  const { createData } = useMutateData('payment/credits', [QUERY_KEYS.credits, userId], 'wallet');
  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<CreditFormInput, unknown, CreditFormData>({
    defaultValues: DEFAULT_FORM_DATA,
    resolver: zodResolver(handleCreditsSchema),
  });

  const walletType = watch('walletType');

  const isButtonDisabled = !watch('amount');
  const isRemoveButtonDisabled = isButtonDisabled || walletType === WALLET_TYPE.BONUS;

  const handleClose = () => {
    closeModal?.();
  };

  const isForAgentPayment = userType === 'partner';

  const onFormSubmit = (creditsData: CreditFormData, actionType: CreditActionType) => {
    if (!userId) return;

    const { isOtherTransaction, amount, ...creditRequestData } = creditsData;

    const transactionType = getTransactionType(isOtherTransaction, walletType, actionType);

    const preparedData: CreditRequestData = {
      ...creditRequestData,
      userId,
      amount,
      isForAgentPayment,
      source: WALLET_TRANSACTION_SOURCE.SPORTSBOOK,
      otherTransactionType: actionType,
      transactionType,
    };

    createData(
      preparedData,
      () => {
        invalidateData([QUERY_KEYS.userBalance, userId]);
        invalidateData([QUERY_KEYS.paymentTransactions]);
        invalidateData([QUERY_KEYS.walletTransactions]);
        handleClose();
      },
      'Balance updated successfully'
    );
  };

  const hasAdminCredentials = hasPermission('defaultAdminRole');
  const isBonusWalletDisabled = !hasAdminCredentials || isForAgentPayment;

  return (
    <>
      <DialogTitle variant='h4' gutterBottom>
        Add/Remove Credits
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <FormSelect
            name='walletType'
            control={control}
            label='Wallet'
            options={WALLET_TYPE_OPTIONS(isBonusWalletDisabled)}
            error={errors.walletType}
            required
          />
          <FormNumberInput
            name='amount'
            control={control}
            label='Amount'
            allowDecimals
            valueAsString
            required
            sx={{ flexGrow: 1 }}
          />

          <TextField {...register('description')} label='Description' multiline fullWidth />
          {hasAdminCredentials && walletType === WALLET_TYPE.MAIN && (
            <Controller
              name='isOtherTransaction'
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label='Other transaction'
                  sx={styles.checkbox}
                />
              )}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button
          onClick={handleSubmit((data) => onFormSubmit(data, CREDIT_ACTION_TYPE.DEPOSIT))}
          disabled={isButtonDisabled}
        >
          Add Credit
        </Button>
        <Button
          color='error'
          onClick={handleSubmit((data) => onFormSubmit(data, CREDIT_ACTION_TYPE.WITHDRAWAL))}
          disabled={isRemoveButtonDisabled}
        >
          Remove Credit
        </Button>
        <Button variant='outlined' onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </>
  );
};

export default HandleCreditsForm;
